import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { compatApi } from '../../utils/Intercepter';
import useTokenStore from '../../stores/tokens';
import { useDaumPostcodePopup } from 'react-daum-postcode';
import { toast } from 'react-toastify';
import toastConfig from '../../const/toastConfig';
import openApiRequest from '../../utils/OpenApiRequest';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';
import OrgSectionTabs from '../../components/OrgSectionTabs';
import DeleteOrganizationConfirmModal from '../../components/deleteOrganizationConfirmModal';

export default function OrganizationInfo() {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useTokenStore();

    const [organization, setOrganization] = useState({
        organizationCode: user.currentOrganizationCode,
        organizationName: user.currentOrganizationName,
        representativeName: '',
        isApproved: '',
        address: '',
        addressDetail: '',
        approvalDate: '',
        registrationDate: '',
        bizRegNo: '',
    });
    const [, setOriginalAddress] = useState('');
    const [addressInput, setAddressInput] = useState({ address: '', addressDetail: '' });
    const [isEditing, setIsEditing] = useState(false);
    const [isEditingRepresentative, setIsEditingRepresentative] = useState(false);
    const [isEditingBizNum, setIsEditingBizNum] = useState(false);
    const [isAddressEditing, setIsAddressEditing] = useState(false);
    const [originalBizRegNo, setOriginalBizRegNo] = useState('');
    const [copied, setCopied] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    useEffect(() => {
        const fetchOrganization = async () => {
            try {
                const orgCode = user?.currentOrganizationCode;
                if (!orgCode) { alert('조직 정보를 가져올 수 없습니다.'); return; }
                const response = await compatApi.post(`/org/organizationInfo`, orgCode);
                if (response.ok) setOrganization(response.data);
                else alert('조직 정보를 가져오는 데 실패했습니다.');
            } catch (error) {
                const status = error.response?.status;
                // 401(인증)·403(인가)은 인터셉터가 처리(토큰 갱신 / 조직 재선택 모달 / 로그아웃).
                // 여기서 별도 alert·navigate 하지 않는다 — 중복 메시지·복구 흐름 충돌 방지.
                if (status !== 401 && status !== 403) { alert('네트워크 오류가 발생했습니다'); }
            }
        };
        fetchOrganization();
    }, [user?.currentOrganizationCode, navigate]);

    const refetchOrganization = async () => {
        try {
            const orgCode = user?.currentOrganizationCode;
            if (!orgCode) return;
            const response = await compatApi.post(`/org/organizationInfo`, orgCode);
            if (response.ok) setOrganization(response.data);
            else alert('조직 정보를 가져오는 데 실패했습니다.');
        } catch (error) {
            const status = error.response?.status;
            if (status !== 401 && status !== 403) { alert('네트워크 오류가 발생했습니다'); }
        }
    };

    const handleNameSave = async () => {
        try {
            const response = await compatApi.post('/org/update-organization-name', {
                organizationCode: user.currentOrganizationCode,
                organizationName: organization.organizationName,
            });
            if (response.ok) {
                setIsEditing(false);
                toast.success('수정되었습니다.', toastConfig);
                await refetchOrganization();
            } else { toast.error('수정 실패했습니다.', toastConfig); }
        } catch { toast.error('수정 중 오류가 발생했습니다.', toastConfig); }
    };

    const handleRepresentativeNameSave = async () => {
        try {
            const response = await compatApi.post('/org/update-representative-name', {
                organizationCode: user.currentOrganizationCode,
                representativeName: organization.representativeName,
            });
            if (response.ok) {
                setIsEditingRepresentative(false);
                toast.success('수정되었습니다.', toastConfig);
                await refetchOrganization();
            } else { toast.error('수정 실패했습니다.', toastConfig); }
        } catch { toast.error('수정 중 오류가 발생했습니다.', toastConfig); }
    };

    const handleBizRegNoSave = async () => {
        if (organization.bizRegNo.length < 10) { toast.warn('사업자 번호를 정확히 입력해 주세요'); return; }
        const businessNumber = organization.bizRegNo.replace(/-/g, '');
        try {
            const res = await compatApi.get('/org/duplicate-bizRegNo/' + organization.bizRegNo);
            if (res.ok) {
                if (res.data) { toast.error('동일한 사업자 번호가 등록 되어있습니다.', toastConfig); return; }
            } else { toast.error('사업자번호 중복 검사에 실패했습니다.', toastConfig); return; }
        } catch { toast.error('수정 중 오류가 발생했습니다.', toastConfig); return; }
        try {
            const statusResponse = await openApiRequest.getStatus({ b_no: [businessNumber] });
            const statusResult = JSON.parse(await statusResponse.text());
            if (statusResponse.ok) {
                const businessStatus = statusResult.data?.[0]?.b_stt;
                if (!businessStatus || businessStatus === '휴업자' || businessStatus === '폐업자') {
                    toast.error(`사업자 상태: ${businessStatus || '조회불가'} - 유효하지 않은 사업자입니다.`); return;
                }
            } else { toast.error('사업자번호 검증 서비스에 연결할 수 없습니다.'); return; }
        } catch { toast.error('수정 중 오류가 발생했습니다.', toastConfig); return; }
        try {
            const response = await compatApi.post('/org/update-bizRegNo', {
                organizationCode: user.currentOrganizationCode,
                bizRegNo: organization.bizRegNo,
            });
            if (response.ok) {
                toast.success('사업자등록번호가 저장되었습니다.', toastConfig);
                await refetchOrganization();
            } else { toast.error('사업자등록번호 등록에 실패했습니다.', toastConfig); }
        } catch { toast.error('업데이트 중 오류가 발생했습니다.', toastConfig); }
        setIsEditingBizNum(false);
    };

    const handleAddressSave = async () => {
        if (!addressInput.address.trim()) { toast.warn('주소를 입력해주세요.', toastConfig); return; }
        try {
            const res = await compatApi.post('/org/update-address', {
                organizationCode: user.currentOrganizationCode,
                address: addressInput.address,
                addressDetail: addressInput.addressDetail,
            });
            if (res.ok) {
                toast.success('주소가 성공적으로 변경되었습니다.', toastConfig);
                await refetchOrganization();
                setIsAddressEditing(false);
            } else { toast.error('주소 변경에 실패했습니다.', toastConfig); }
        } catch { toast.error('주소 변경 중 네트워크 오류가 발생했습니다.', toastConfig); }
    };

    const handleDeleteOrganization = async (confirmed) => {
        if (!confirmed) return;
        try {
            const response = await compatApi.post('/org/deleteOrganization', {
                organizationCode: user.currentOrganizationCode,
            });
            if (response.ok) {
                toast.success('조직이 삭제되었습니다.', toastConfig);
                // 삭제된 조직이 선택 조직으로 남아 재진입 시 403(ORG_ACCESS_REVOKED)을 유발하지 않도록 정리
                localStorage.removeItem('currentOrganizationCode');
                localStorage.removeItem('currentOrganizationName');
                setTimeout(() => { window.location.href = '/'; }, 500);
            } else { toast.error('조직 삭제에 실패했습니다.', toastConfig); }
        } catch { toast.error('조직 삭제 중 오류가 발생했습니다.', toastConfig); }
    };

    const popupOpen = useDaumPostcodePopup();
    const handleComplete = (data) => {
        let fullAddress = data.address;
        let extraAddress = '';
        if (data.addressType === 'R') {
            if (data.bname !== '') extraAddress += data.bname;
            if (data.buildingName !== '') extraAddress += (extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName);
            if (extraAddress !== '') fullAddress += ` (${extraAddress})`;
        }
        setAddressInput(prev => ({ ...prev, address: fullAddress }));
    };

    const copyCode = () => {
        navigator.clipboard?.writeText(organization.organizationCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
    };

    const isAdmin = isAuthenticated && (user.currentRole === 'ADMIN' || user.currentRole === 'MASTER');

    const getOperationMonths = () => {
        if (!organization.registrationDate) return null;
        const start = new Date(organization.registrationDate);
        const now = new Date();
        const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
        return months > 0 ? months : null;
    };
    const opMonths = getOperationMonths();

    const inputS = {
        height: 36, borderRadius: 8, border: '1.5px solid #ececf2', background: '#fafaff',
        padding: '0 12px', fontSize: 13, color: '#1f1d2e', outline: 'none',
        fontFamily: 'inherit', flex: 1,
    };
    const btnSm = (variant = 'primary') => ({
        height: 32, padding: '0 14px', borderRadius: 8, fontSize: 12.5,
        fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
        border: variant === 'primary' ? 'none' : `1.5px solid ${variant === 'danger' ? '#ef4444' : '#ececf2'}`,
        background: variant === 'primary' ? `linear-gradient(135deg, ${PURPLE}, ${MINT})`
            : variant === 'danger' ? '#fee2e2' : 'white',
        color: variant === 'primary' ? 'white' : variant === 'danger' ? '#dc2626' : '#5d5b6e',
        boxShadow: variant === 'primary' ? `0 4px 10px -4px ${hexToRgba(PURPLE, .55)}` : 'none',
    });

    return (
        <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
            <main style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>
                <OrgSectionTabs active="org-info" />

                {/* Hero card */}
                <div style={{
                    background: 'white', borderRadius: 18,
                    border: '1px solid #f0eff5',
                    boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                    overflow: 'hidden', marginBottom: 16,
                }}>
                    <div style={{
                        position: 'relative',
                        background: `linear-gradient(135deg, ${hexToRgba(PURPLE, .92)} 0%, ${hexToRgba(MINT, .85)} 100%)`,
                        padding: '26px 28px 110px', color: 'white',
                    }}>
                        <svg width="160" height="160" viewBox="0 0 160 160" style={{ position: 'absolute', right: -20, top: -30, opacity: .35, pointerEvents: 'none' }}>
                            <defs>
                                <pattern id="dotpat" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
                                    <circle cx="2" cy="2" r="1.4" fill="white" />
                                </pattern>
                            </defs>
                            <rect width="160" height="160" fill="url(#dotpat)" />
                        </svg>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, position: 'relative' }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: .85 }}>
                                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
                            </svg>
                            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', opacity: .85 }}>Organization</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
                            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: '-0.01em' }}>{organization.organizationName}</h2>
                            <span style={{
                                fontSize: 10.5, fontWeight: 700, color: 'white',
                                background: 'rgba(255,255,255,.22)', border: '1px solid rgba(255,255,255,.35)',
                                padding: '3px 9px', borderRadius: 999, letterSpacing: '.08em', backdropFilter: 'blur(4px)',
                            }}>{user.currentRole}</span>
                        </div>
                        <div style={{ marginTop: 6, fontSize: 13, opacity: .88, display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
                            <span>조직 코드</span>
                            <code style={{
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                background: 'rgba(255,255,255,.22)', padding: '2px 8px', borderRadius: 6,
                                fontSize: 13, letterSpacing: '.05em', fontWeight: 600,
                            }}>{organization.organizationCode}</code>
                            <button onClick={copyCode} style={{
                                background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.32)',
                                color: 'white', borderRadius: 6, padding: '2px 8px', cursor: 'pointer',
                                fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                            }}>
                                <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="5" y="5" width="9" height="9" rx="1.5" />
                                    <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" />
                                </svg>
                                {copied ? '복사됨!' : '복사'}
                            </button>
                        </div>
                    </div>

                    {/* Quick stats */}
                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        background: 'white',
                        margin: '-72px 22px 22px',
                        borderRadius: 14, border: '1px solid #f0eff5',
                        boxShadow: '0 8px 24px -10px rgba(60,50,120,.18)',
                        overflow: 'hidden', position: 'relative',
                    }}>
                        <QuickStat label="관리자 승인" value={organization.isApproved === 'Y' ? '승인 완료' : '대기 중'} accent={organization.isApproved === 'Y' ? MINT : '#f59e0b'}
                            icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6z" /></svg>}
                        />
                        <QuickStat label="사업자 등록" value={organization.bizRegNo ? '등록 완료' : '미등록'} accent={organization.bizRegNo ? MINT : '#8b8a99'}
                            icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></svg>}
                        />
                        <QuickStat label="운영 기간" value={opMonths ? String(opMonths) : '-'} suffix={opMonths ? '개월' : ''} accent={PURPLE}
                            icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>}
                        />
                    </div>
                </div>

                {/* Two-column: details + side panel */}
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.65fr) minmax(0, 1fr)', gap: 16 }}>
                    {/* Detail card */}
                    <div style={{
                        background: 'white', borderRadius: 18,
                        border: '1px solid #f0eff5',
                        boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            padding: '16px 22px',
                            background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)',
                            borderBottom: '1px solid #ececf2',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>기본 정보</h3>
                            {organization.registrationDate && (
                                <span style={{ fontSize: 11.5, color: '#8b8a99' }}>생성일 · {organization.registrationDate}</span>
                            )}
                        </div>

                        {/* 조직 이름 */}
                        <DetailRow label="조직 이름"
                            value={
                                isAdmin && isEditing ? (
                                    <div style={{ display: 'flex', gap: 6, flex: 1 }}>
                                        <input type="text" value={organization.organizationName}
                                            onChange={(e) => setOrganization(prev => ({ ...prev, organizationName: e.target.value }))}
                                            style={inputS}
                                            onFocus={e => { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 3px ${hexToRgba(PURPLE, .1)}`; }}
                                            onBlur={e => { e.target.style.borderColor = '#ececf2'; e.target.style.boxShadow = 'none'; }}
                                        />
                                        <button style={btnSm('primary')} onClick={handleNameSave}>저장</button>
                                        <button style={btnSm('danger')} onClick={() => { setIsEditing(false); setOrganization(prev => ({ ...prev, organizationName: user.currentOrganizationName })); }}>취소</button>
                                    </div>
                                ) : organization.organizationName
                            }
                            action={isAdmin && !isEditing ? '변경' : null}
                            onAction={() => setIsEditing(true)}
                        />

                        {/* 대표자명 */}
                        <DetailRow label="대표자명"
                            value={
                                isAdmin && isEditingRepresentative ? (
                                    <div style={{ display: 'flex', gap: 6, flex: 1 }}>
                                        <input type="text" value={organization.representativeName || ''}
                                            onChange={(e) => setOrganization(prev => ({ ...prev, representativeName: e.target.value }))}
                                            placeholder="대표자명" style={inputS}
                                            onFocus={e => { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 3px ${hexToRgba(PURPLE, .1)}`; }}
                                            onBlur={e => { e.target.style.borderColor = '#ececf2'; e.target.style.boxShadow = 'none'; }}
                                        />
                                        <button style={btnSm('primary')} onClick={handleRepresentativeNameSave}>저장</button>
                                        <button style={btnSm('danger')} onClick={() => setIsEditingRepresentative(false)}>취소</button>
                                    </div>
                                ) : (organization.representativeName || null)
                            }
                            action={isAdmin && !isEditingRepresentative ? (organization.representativeName ? '변경' : '등록') : null}
                            onAction={() => setIsEditingRepresentative(true)}
                        />

                        {/* 조직 코드 */}
                        <DetailRow label="조직 코드" value={organization.organizationCode} mono />

                        {/* 사업자 등록 */}
                        <DetailRow label="사업자 등록번호"
                            value={
                                isAdmin && isEditingBizNum ? (
                                    <div style={{ display: 'flex', gap: 6, flex: 1 }}>
                                        <input type="text" value={organization.bizRegNo}
                                            onChange={(e) => {
                                                let v = e.target.value.replace(/\D/g, '');
                                                if (v.length <= 3) { /* keep */ }
                                                else if (v.length <= 5) v = v.slice(0, 3) + '-' + v.slice(3);
                                                else v = v.slice(0, 3) + '-' + v.slice(3, 5) + '-' + v.slice(5, 10);
                                                setOrganization(prev => ({ ...prev, bizRegNo: v }));
                                            }}
                                            placeholder="xxx-xx-xxxxx" style={inputS}
                                            onFocus={e => { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 3px ${hexToRgba(PURPLE, .1)}`; }}
                                            onBlur={e => { e.target.style.borderColor = '#ececf2'; e.target.style.boxShadow = 'none'; }}
                                        />
                                        <button style={btnSm('primary')} onClick={handleBizRegNoSave}>저장</button>
                                        <button style={btnSm('danger')} onClick={() => { setIsEditingBizNum(false); setOrganization(prev => ({ ...prev, bizRegNo: originalBizRegNo })); }}>취소</button>
                                    </div>
                                ) : (organization.bizRegNo || null)
                            }
                            action={isAdmin && !isEditingBizNum && !organization.bizRegNo ? '등록' : null}
                            actionVariant="primary"
                            onAction={() => { setOriginalBizRegNo(organization.bizRegNo); setIsEditingBizNum(true); }}
                        />

                        {/* 관리자 승인 */}
                        <DetailRow label="관리자 승인"
                            value={
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                        fontSize: 11.5, fontWeight: 600,
                                        color: organization.isApproved === 'Y' ? MINT : '#f59e0b',
                                        background: organization.isApproved === 'Y' ? hexToRgba(MINT, .14) : 'rgba(245,158,11,.12)',
                                        padding: '3px 9px', borderRadius: 999,
                                    }}>
                                        {organization.isApproved === 'Y' ? (
                                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                                <path d="M2 6.5L5 9L10 3" stroke={MINT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                                <circle cx="6" cy="6" r="4.5" stroke="#f59e0b" strokeWidth="1.5" />
                                                <path d="M6 4v3" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" />
                                                <circle cx="6" cy="8.5" r=".6" fill="#f59e0b" />
                                            </svg>
                                        )}
                                        {organization.isApproved === 'Y' ? '승인 완료' : '승인 대기'}
                                    </span>
                                    {organization.approvalDate && (
                                        <span style={{ fontSize: 12.5, color: '#8b8a99' }}>승인일 {organization.approvalDate}</span>
                                    )}
                                </span>
                            }
                        />

                        {/* 주소 */}
                        <DetailRow label="주소"
                            value={
                                isAdmin && isAddressEditing ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <input type="text" value={addressInput.address} readOnly placeholder="주소"
                                                style={{ ...inputS, background: '#f5f4fa', cursor: 'default' }} />
                                            <button style={btnSm('outline')} onClick={() => popupOpen({ onComplete: handleComplete })}>검색</button>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <input type="text" value={addressInput.addressDetail}
                                                onChange={(e) => setAddressInput(prev => ({ ...prev, addressDetail: e.target.value }))}
                                                placeholder="상세 주소" style={inputS}
                                                onFocus={e => { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 3px ${hexToRgba(PURPLE, .1)}`; }}
                                                onBlur={e => { e.target.style.borderColor = '#ececf2'; e.target.style.boxShadow = 'none'; }}
                                            />
                                            <button style={btnSm('primary')} onClick={handleAddressSave}>저장</button>
                                            <button style={btnSm('danger')} onClick={() => { setIsAddressEditing(false); setAddressInput({ address: '', addressDetail: '' }); }}>취소</button>
                                        </div>
                                    </div>
                                ) : (organization.address ? `${organization.address}${organization.addressDetail ? ' ' + organization.addressDetail : ''}` : null)
                            }
                            action={isAdmin && !isAddressEditing ? '변경' : null}
                            onAction={() => {
                                setOriginalAddress(organization.address);
                                setAddressInput({ address: organization.address || '', addressDetail: organization.addressDetail || '' });
                                setIsAddressEditing(true);
                            }}
                        />

                        {/* 조직 생성일 */}
                        <DetailRow label="조직 생성일" last
                            value={
                                organization.registrationDate ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8b8a99" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" />
                                        </svg>
                                        {organization.registrationDate}
                                        {opMonths && <span style={{ fontSize: 11.5, color: '#aaa9b7' }}>(약 {opMonths}개월 전)</span>}
                                    </span>
                                ) : null
                            }
                        />
                    </div>

                    {/* Side column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {isAdmin && (
                            <div style={{
                                background: 'white', borderRadius: 18,
                                border: '1px solid #f0eff5',
                                boxShadow: '0 1px 2px rgba(60,50,120,.04)',
                                padding: '18px 20px',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                                    <div style={{
                                        width: 26, height: 26, borderRadius: 7,
                                        background: hexToRgba(PURPLE, .12), color: PURPLE,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 2l2.4 5 5.6.8-4 3.9 1 5.5L12 14.8 7 17.2l1-5.5L4 7.8 9.6 7z" />
                                        </svg>
                                    </div>
                                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#1f1d2e' }}>관리자 작업</h3>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <SideAction label="조직 정보 수정" hint="이름, 대표자, 주소, 사업자번호"
                                        icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" /></svg>}
                                        onClick={() => setIsEditing(true)}
                                    />
                                    <SideAction label="조직 삭제" hint="복구할 수 없습니다" danger
                                        icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>}
                                        onClick={() => setDeleteModalOpen(true)}
                                    />
                                </div>
                            </div>
                        )}

                        {/* TIP */}
                        <div style={{
                            borderRadius: 18, padding: '16px 18px',
                            background: `linear-gradient(135deg, ${hexToRgba(PURPLE, .06)}, ${hexToRgba(MINT, .05)})`,
                            border: `1px dashed ${hexToRgba(PURPLE, .20)}`,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill={PURPLE} style={{ flexShrink: 0 }}>
                                    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
                                </svg>
                                <span style={{ fontSize: 12, fontWeight: 700, color: PURPLE, letterSpacing: '.04em' }}>TIP</span>
                            </div>
                            <p style={{ margin: 0, fontSize: 12.5, color: '#3a3849', lineHeight: 1.55 }}>
                                <b>사업자 등록</b>을 마치면 세금계산서·전자결재 발행이 활성화되고
                                구성원의 연봉협상일 자동 알림 기능이 켜집니다.
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            <DeleteOrganizationConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleDeleteOrganization}
                organizationCode={organization.organizationCode}
            />
        </div>
    );
}

function DetailRow({ label, value, action, actionVariant = 'outline', mono, onAction, last }) {
    const empty = !value || value === '-';
    return (
        <div style={{
            display: 'grid', gridTemplateColumns: '180px 1fr auto',
            alignItems: 'center', gap: 16,
            padding: '16px 22px',
            borderBottom: last ? 'none' : '1px solid #f4f3f9',
        }}>
            <div style={{ fontSize: 13, color: '#8b8a99', fontWeight: 500 }}>{label}</div>
            <div style={{
                fontSize: 14, color: empty ? '#c7c5d2' : '#1f1d2e',
                fontWeight: empty ? 400 : 500,
                fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
                letterSpacing: mono ? '.02em' : 0,
                wordBreak: 'break-all',
            }}>{empty ? '등록되지 않음' : value}</div>
            {action ? (
                <button
                    onClick={onAction}
                    style={actionVariant === 'primary' ? {
                        height: 32, padding: '0 14px', borderRadius: 8, border: 'none',
                        background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                        color: 'white', fontSize: 12.5, fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                        boxShadow: `0 4px 10px -4px ${hexToRgba(PURPLE, .55)}`,
                    } : {
                        height: 32, padding: '0 14px', borderRadius: 8,
                        background: 'white', border: '1px solid #ececf2',
                        color: '#5d5b6e', fontSize: 12.5, fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                        transition: 'all 120ms',
                    }}
                    onMouseEnter={(e) => {
                        if (actionVariant !== 'primary') { e.currentTarget.style.borderColor = PURPLE; e.currentTarget.style.color = PURPLE; }
                    }}
                    onMouseLeave={(e) => {
                        if (actionVariant !== 'primary') { e.currentTarget.style.borderColor = '#ececf2'; e.currentTarget.style.color = '#5d5b6e'; }
                    }}
                >{action}</button>
            ) : <div />}
        </div>
    );
}

function QuickStat({ label, value, suffix, icon, accent }) {
    return (
        <div style={{
            padding: '14px 18px',
            borderRight: '1px solid #f4f3f9',
            display: 'flex', alignItems: 'center', gap: 11,
        }}>
            <div style={{
                width: 34, height: 34, borderRadius: 9,
                background: hexToRgba(accent, .12), color: accent,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
            }}>{icon}</div>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
                <span style={{ fontSize: 11.5, color: '#8b8a99', fontWeight: 500 }}>{label}</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#1f1d2e', marginTop: 3 }}>
                    {value}{suffix && <span style={{ fontSize: 11.5, color: '#8b8a99', fontWeight: 500, marginLeft: 3 }}>{suffix}</span>}
                </span>
            </div>
        </div>
    );
}

function SideAction({ label, hint, icon, danger, onClick }) {
    return (
        <button
            onClick={onClick}
            style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10,
                background: 'transparent', border: '1px solid #ececf2',
                cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                transition: 'all 120ms', width: '100%',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = danger ? '#ef4444' : PURPLE;
                e.currentTarget.style.background = danger ? 'rgba(239,68,68,.04)' : hexToRgba(PURPLE, .05);
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#ececf2';
                e.currentTarget.style.background = 'transparent';
            }}
        >
            <div style={{
                width: 30, height: 30, borderRadius: 8,
                background: danger ? 'rgba(239,68,68,.10)' : hexToRgba(PURPLE, .10),
                color: danger ? '#ef4444' : PURPLE,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
            }}>{icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: danger ? '#ef4444' : '#1f1d2e' }}>{label}</div>
                <div style={{ fontSize: 11.5, color: '#8b8a99', marginTop: 1 }}>{hint}</div>
            </div>
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#c7c5d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
                <path d="M4 6l4 4 4-4" />
            </svg>
        </button>
    );
}
